import { useEffect, useState, useCallback, useRef } from "react";

const usb: any = (navigator as any)?.usb;

type PrinterStatus = "idle" | "selecting" | "connected" | "ready" | "error";
type LastError = { code: string; message: string } | null;

const useThermalPrinter = () => {
  const [device, setDevice] = useState<any>(null);
  const [endpointOut, setEndpointOut] = useState<number | null>(null);
  const [status, setStatus] = useState<PrinterStatus>("idle");
  const [lastError, setLastError] = useState<LastError>(null);

  const deviceRef = useRef<any>(null);
  const outRef = useRef<number | null>(null);

  const setDev = (d: any) => {
    setDevice(d);
    deviceRef.current = d;
  };
  const setOut = (n: number | null) => {
    setEndpointOut(n);
    outRef.current = n;
  };

  const initialize = useCallback(async () => {
    try {
      if (!usb) {
        setLastError({
          code: "unsupported",
          message: "WebUSB is not available in this browser/context.",
        });
        setStatus("error");
        return null;
      }
      const devs = await usb.getDevices();
      const dev = devs?.[0] ?? null;
      setDev(dev);
      if (dev) setStatus("connected");
      return dev;
    } catch (e: any) {
      setLastError({
        code: "enumerate-failed",
        message: e?.message ?? "Could not enumerate USB devices.",
      });
      setStatus("error");
      return null;
    }
  }, []);

  const configure = useCallback(async () => {
    if (!usb) {
      setLastError({ code: "unsupported", message: "WebUSB not available." });
      setStatus("error");
      return null;
    }
    try {
      setStatus("selecting");
      const dev = await usb.requestDevice({ filters: [] }); // will throw if user cancels
      setDev(dev);
      setStatus("connected");
      return dev;
    } catch (e: any) {
      // User closed the chooser or no device picked
      if (e?.name === "NotFoundError") {
        setLastError({ code: "no-selection", message: "No device selected." });
        setStatus("idle");
        return null; // <- IMPORTANT: swallow, don't throw
      }
      setLastError({
        code: "request-failed",
        message: e?.message ?? "Failed to request USB device.",
      });
      setStatus("error");
      return null;
    }
  }, []);

  const openAndClaim = useCallback(async (dev: any) => {
    try {
      if (!dev.opened) await dev.open();
      if (!dev.configuration) await dev.selectConfiguration(1);

      let ifaceNumber: number | null = null;
      let altSetting: number | null = null;
      let outEp: number | null = null;

      for (const iface of dev.configuration.interfaces) {
        for (const alt of iface.alternates) {
          const ep = (alt.endpoints || []).find(
            (e: any) => e.direction === "out"
          );
          if (ep) {
            ifaceNumber = iface.interfaceNumber;
            altSetting = alt.alternateSetting;
            outEp = ep.endpointNumber;
            break;
          }
        }
        if (outEp != null) break;
      }

      if (ifaceNumber == null || altSetting == null || outEp == null) {
        throw new Error("No OUT endpoint found on the printer.");
      }

      await dev.claimInterface(ifaceNumber);
      await dev.selectAlternateInterface(ifaceNumber, altSetting);
      setOut(outEp);
      setDev(dev);
      setStatus("ready");
      return true;
    } catch (e: any) {
      setLastError({
        code: "claim-failed",
        message: e?.message ?? "Failed to open/claim the USB device.",
      });
      setStatus("error");
      return false;
    }
  }, []);

  const connect = useCallback(async (): Promise<boolean> => {
    // Try existing device
    let dev = deviceRef.current;
    if (!dev) dev = await initialize();
    if (!dev) dev = await configure(); // may return null if user cancels
    if (!dev) return false; // <- safe early return, no throw
    return openAndClaim(dev);
  }, [initialize, configure, openAndClaim]);

  // Print returns boolean and NEVER throws for user-cancel paths
  const print = useCallback(
    async (data: Uint8Array): Promise<boolean> => {
      try {
        const ok = await connect();
        if (!ok) return false;

        const dev = deviceRef.current;
        const out = outRef.current;
        if (!dev || out == null) {
          setLastError({ code: "not-ready", message: "Printer not ready." });
          setStatus("error");
          return false;
        }
        await dev.transferOut(out, data);
        return true;
      } catch (e: any) {
        setLastError({
          code: "print-failed",
          message: e?.message ?? "Failed to print.",
        });
        setStatus("error");
        return false;
      }
    },
    [connect]
  );

  useEffect(() => {
    initialize();
  }, [initialize]);

  const resetError = () => setLastError(null);

  return {
    initialize,
    configure,
    connect,
    print, // Promise<boolean>
    isConfigured: !!device,
    status, // "idle" | "selecting" | "connected" | "ready" | "error"
    lastError, // { code, message } | null
    resetError,
  };
};

export default useThermalPrinter;
